// src/app/appModals.js
// App.js'ten ayrıştırılan modal render mantığı
// closeModal, modalReturnRef, pushedByAppRef App.js'te kalır — bu dosya sadece JSX render'ı taşır
//
//   Bottom-nav "+" → "Clip" seçeneği MyClips V3 UploadComposerV3 render eder.

import React, { lazy } from "react";
import type { DocumentData } from "firebase/firestore";
import { isPostUploading } from "../services/postUploadGuard";
import { readPostNavContext, clearPostNavContext } from "../postNavContext";
// FAZ 6 (6.5 bundle) — ağır modallar React.lazy'ye çevrildi (~25 kB gzip
// ana bundle'dan çıktı). Render zaten App.js'teki <Suspense fallback={null}>
// içinde; modal ilk açılışta milisaniyelik gecikmeyle chunk'ını çeker.
// CreateMenu bilinçli eager: "+" menüsü anında açılmalı, boyutu önemsiz.
import CreateMenu from "../CreateMenu";
// EMİR #94.4 — derin bağlantı çözülemediğinde gösterilen durum.
// 🔒 STATİK import (lazy DEĞİL): ağ yokken gösterilecek bir ekranın chunk'ını
// o an ağdan indirmeye çalışmak, hatayı hatayla karşılamaktır (#94.3 kuralı).
import DerinBaglantiDurumu from "../components/DerinBaglantiDurumu";
// Composer/kamera + check-in URL'i (tek kaynak: src/app/olusturUrl.ts).
import { olusturModaliMi, olusturUrlTemizle, olusturUrlYaz } from "../app/olusturUrl";
// #888 — «Tekrar dene» SPA içinde yeniden çözümleme yapar (tam yenileme son çare).
import { derinBaglantiyiYenidenDene } from "./appRouting";
import { dmKatmaniKapat } from "./dmGecmisi";
// P0-4 — «Oluştur → Hikaye» maddesinin gerçek hedefi (ölü düğme onarımı).
import { hikayeYuklemeyiAc } from "./hikayeGirisi";
import { showToast } from "../ToastBoot";
// Bildirim yolunun TEK yüklemi (regex kopyası YOK — bkz. gorunumFiltreUrl).
import { bildirimYolundaMi } from "./gorunumFiltreUrl";
import type { OlusturModali } from "../app/olusturUrl";
const NewPost = lazy(() => import("../NewPost"));
const Bildirimler: any = lazy(() => import("../Bildirimler"));
const CheckInModal = lazy(() => import("../CheckInModal"));
const NewCheckInDetail = lazy(() => import("../NewCheckInDetail"));
const PlaceDetailModal = lazy(() => import("../PlaceDetailModal"));
const PlaceDetailModalMobile = lazy(() => import("../PlaceDetailModalMobile"));
const MapSettingsModal = lazy(() => import("../MapSettingsModal"));
const FriendPickerModal = lazy(() => import("../FriendPickerModal"));
const KullaniciProfili: any = lazy(() => import("../KullaniciProfili"));
// Cold-load / V3-feed avatarı story yolu: legacy StoryModal overlay (sticker/
// anket/5★/reaksiyon) RENDER ETMİYORDU → paylaşılan /stories/ linki feature-
// stripped görünüyordu. Tam özellikli StoryModalMobile/Desktop'a yönlendirildi
// (hikaye tepsisiyle aynı bileşenler; props birebir uyumlu).
const StoryModalMobile = lazy(() => import("../StoryModalMobile"));
const StoryModalDesktop: any = lazy(() => import("../StoryModalDesktop"));
// P6-2 — Eski Messages.js aslında post composer'dı (DM değil). Gerçek
// DM inbox'a (DmInbox) bağlandı.
const DmInbox = lazy(() => import("../components/dm/DmInbox"));
const PostDetailModal: any = lazy(() => import("../PostDetailModal"));
const ClipDetailModalMobile = lazy(() => import("../ClipDetailModalMobile"));
const ClipDetailModalDesktop: any = lazy(() => import("../ClipDetailModalDesktop"));

const DiscoveryMobile = lazy(() => import("../DiscoveryMobile"));
// V3 Upload Composer — V3 flag açıkken kullanılır.
const UploadComposerV3 = lazy(() => import("../myclipsv3/compose/UploadComposerV3"));

// Mert Dolapcıoğlu çizimleri — her açılışta random biri seçilir
const MERT_IMGS = [
  '/mert/mert-1.webp', '/mert/mert-2.webp', '/mert/mert-3.webp', '/mert/mert-4.webp',
  '/mert/mert-5.webp', '/mert/mert-6.webp', '/mert/mert-7.webp', '/mert/mert-8.webp',
  '/mert/mert-9.webp', '/mert/mert-10.webp'
];

// ── TİPLER (import'lardan SONRA, ilk kullanımdan ÖNCE) ──────────────────
//
// ⚠️ `modalData` GERÇEKTE `modalContent` ile AYRIMLANMIŞ bir birleşimdir, ama
//    kod ayrımı `modalData` üzerinde DEĞİL `modalContent` üzerinde yapar
//    (`if (modalContent === "placeDetail")`). TS iki AYRI değişken arasında bu
//    bağı kuramaz — ve KAYNAK METNİ de değiştirilemez: iki SÖZLEŞME TESTİ
//    (`app/mekanUrlDevri.test.ts:145`, `app/sohbetUrl.test.ts:153`)
//    `placeId={modalData?.placeId}` ve `initialMatchId={modalData?.matchId ||
//    null}` ifadelerini BİREBİR arıyor. Bu yüzden erişimler `as` ile
//    daraltılamaz; çözüm, birleşimin HER ÜYESİNİN alanları taşıması.
// 📏 `string` dalı uydurma değil: `App.handleStartMessage` (`setModalData(userId)`)
//    ve profil modalı düz uid yazar; kod bunu 351/353'te `typeof` ile ayırır.
//    `string & Partial<ModalNesnesi>` bu iki gerçeği AYNI ANDA tutar:
//    `typeof modalData === "string"` dalı hâlâ `string`e daralır, ama `?.`
//    erişimi de derlenir. Runtime'da hiçbir karşılığı yok (salt tip).

/** `modalData` nesne olduğunda okunan alanların BİRLEŞİK yüzeyi (hepsi opsiyonel). */
type ModalNesnesi = {
  /** derinBaglantiDurumu */
  derinDurum?: string;
  /** derinBaglantiDurumu — NE bulunamadı: içerik mi, rota mı (ölü rota kapısı). */
  derinTur?: string;
  /** placeDetail */
  placeId?: string;
  placeName?: string;
  name?: string;
  address?: string;
  coords?: { lat: number; lng: number } | null;
  /** discovery */
  matchId?: string | null;
  /** viewingStory */
  stories?: unknown;
  kullaniciAdi?: string;
  profilFoto?: string;
  initialIndex?: number;
  /** messages (DM) */
  conversationId?: string | null;
  /** viewingClip / viewingComments — bütün nesne alt bileşene devrediliyor */
  id?: string;
};

type ModalVerisi =
  | ModalNesnesi
  | (string & Partial<ModalNesnesi>)
  | null
  | undefined;

type ModalParams = {
  modalContent: string | null | undefined;
  modalData: ModalVerisi;
  /** Firebase `User`. NULL DEĞİL: `App.tsx:889` (`if (!user) return <Auth />`)
   *  `renderModal()` çağrısından ÖNCE duruyor — bu dosyanın gövdesi de zaten
   *  `user.uid`i guard'sız okuyor. `| null` yazmak gövdeyi yalanlardı. */
  user: { uid: string };
  currentUserProfile: DocumentData | null | undefined;
  isMobile: boolean;
  refs: {
    modalReturnRef: React.MutableRefObject<{
      modalContent?: string | null;
      modalData?: ModalVerisi;
      pushedByApp?: boolean;
    } | null>;
    pushedByAppRef: React.MutableRefObject<boolean>;
  };
  setters: {
    setModalContent: (content: string | null) => void;
    setModalData: (data: ModalVerisi) => void;
    setActivePage: (page: string) => void;
    /**
     * #888 — «Tekrar dene» artık SPA içinde yeniden çözümleme yapıyor
     * (`derinBaglantiyiYenidenDene`). Çözücünün sözleşmesi bu setter'ı
     * ZORUNLU kılıyor; opsiyonel bırakmak retry'ın profil dalını sessizce
     * ölü bırakırdı.
     */
    setProfileRouteUsername: (username: string) => void;
  };
  handlers: {
    handleViewProfile: (userId?: string) => void;
    handleStartMessage: (userId?: string) => void;
    handlePlaceSelectForCheckIn: (place: ModalVerisi) => void;
    handleOpenFriendPicker: () => void;
    handleSaveFriendSelection: (uids: string[]) => void;
    handleViewUserFromPlace: (userId?: string) => void;
    /** ⚠️ ÖLÇÜLDÜ (#832): profil MODALINDAKİ (`KullaniciProfili`) check-in
     *  mekân butonu da ölüydü — `KullaniciProfili` prop'u `ProfileMobile`e
     *  devrediyordu ama TEK render yeri (bu dosya) onu HİÇ geçmiyordu.
     *  İmza `App.handleViewPlaceDetail` (App.tsx:735) ile birebir. */
    handleViewPlaceDetail: (
      placeData:
        | { placeId?: string; refId?: string; id?: string; name?: string | null }
        | null
        | undefined,
    ) => void;
  };
  isAnyPermalinkPath: (pathname: string) => boolean;
};

// Discovery modal açıkken görsel sabit kalsın — her render'da değişmesin
let _cachedMertImg: string | null = null;
let _lastDiscoveryOpen = false;

const MODAL_STYLE = {
  display: "flex",
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "rgba(0, 0, 0, 0.65)",
  zIndex: 2000,
} as React.CSSProperties;

const COMMENTS_MODAL_STYLE = {
  ...MODAL_STYLE,
  alignItems: "flex-end",
  backgroundColor: "rgba(0, 0, 0, 0.5)",
};

/**
 * renderModal — modalContent'e göre doğru modal bileşenini döndürür.
 *
 * @param {object} params
 * @param {string|null} params.modalContent
 * @param {any}         params.modalData
 * @param {object}      params.user
 * @param {object}      params.currentUserProfile
 * @param {boolean}     params.isMobile
 * @param {object}      params.refs — { modalReturnRef, pushedByAppRef }
 * @param {object}      params.setters — { setModalContent, setModalData, setActivePage }
 * @param {object}      params.handlers — { handleViewProfile, handleStartMessage, handlePlaceSelectForCheckIn, handleOpenFriendPicker, handleSaveFriendSelection, handleViewUserFromPlace }
 * @param {Function}    params.isAnyPermalinkPath
 */
export function renderModal({
  modalContent,
  modalData,
  user,
  currentUserProfile,
  isMobile,
  refs,
  setters,
  handlers,
  isAnyPermalinkPath,
}: ModalParams) {
  if (!modalContent) return null;

  const { modalReturnRef, pushedByAppRef } = refs;
  const { setModalContent, setModalData, setActivePage, setProfileRouteUsername } = setters;
  const {
    handleViewProfile,
    handleStartMessage,
    handlePlaceSelectForCheckIn,
    handleOpenFriendPicker,
    handleSaveFriendSelection,
    handleViewUserFromPlace,
    handleViewPlaceDetail,
  } = handlers;

  const closeModal = () => {
    if (
      modalContent === "viewingProfile" ||
      modalContent === "placeDetail" ||
      modalContent === "viewingStory"
    ) {
      // Açılışta /u/{uid}, /place/{id} veya /stories/{u}/{id} push edildi → X
      // (kapat) de donanım GERİ ile AYNI yoldan gitsin: history.back() popstate'i
      // tetikler, o da modalı kapatıp önceki durumu restore eder + URL'yi geri alır.
      if (pushedByAppRef.current) {
        try { window.history.back(); return; } catch (_) { /* fallback aşağıda */ }
      }
      // Fallback (push edilmediyse / history yoksa): doğrudan kapat + restore.
      const ret = modalReturnRef.current;
      modalReturnRef.current = null;
      setModalContent(ret?.modalContent || null);
      setModalData(ret?.modalData || null);
      pushedByAppRef.current = !!ret?.pushedByApp;
      return;
    }
    // Clear the desktop post navigation context when the post modal closes.
    if (modalContent === "viewingComments") {
      clearPostNavContext();
    }
    const pathname = window.location.pathname;
    // ── DM KATMANININ ÜÇÜNCÜ KAPATICISI (arka plan tıklaması) ─────────────
    // X ve Escape `DmInbox.requestClose`tan, yani `dmKatmaniKapat`tan geçer.
    // Arka plan tıklaması buraya DOĞRUDAN düşüyordu ve o yol URL'e HİÇ
    // dokunmuyordu: `/direct/t/{id}` adres çubuğunda kalıyor, yenilemede DM
    // yeniden açılıyordu. Sohbet artık PUSH'landığı için bu delik büyürdü —
    // geride bayat bir `/direct` girdisi kalır ve GERİ tuşu DM'i YENİDEN
    // AÇARDI (düzeltmenin, düzelttiğinden kötü bir kusur üretmesi).
    // 🧭 KANON "susturma yollarını say": üç kapatıcının ÜÇÜ de aynı yoldan.
    // NOT: `requestClose`tan gelindiğinde URL zaten `/` olduğu için bu dal
    // ATLANIR (çift kapanış yok) ve akış aşağıdaki normal kuyruğa düşer.
    if (modalContent === "messages" && /^\/direct/.test(pathname)) {
      dmKatmaniKapat(() => {
        try { window.history.replaceState(window.history.state, "", "/"); } catch (_) { /* no-op */ }
        setModalContent(null);
        setModalData(null);
        pushedByAppRef.current = false;
      });
      return;
    }
    const isPermalink = isAnyPermalinkPath(pathname);
    if (isPermalink) {
      if (pushedByAppRef.current) window.history.back();
      else window.history.replaceState({}, "", "/");
    }
    // PR-18 — Bildirimler X ile kapatılınca /notifications URL'si temizlenir
    // (DmInbox /direct kapanış deseni: replaceState → history.back belirsizliği
    // yok; bayat URL kalırsa yenileme modalı yeniden açardı).
    if (
      modalContent === "notifications" &&
      bildirimYolundaMi(pathname)
    ) {
      try { window.history.replaceState(window.history.state, "", "/"); } catch (_) { /* no-op */ }
    }
    // Oluşturma katmanı (composer/kamera/check-in) kapanışı — bildirimlerin
    // ölçülmüş deseni: `replaceState` (history.back DEĞİL). Composer birden çok
    // giriş açmış olabilir (`/create` → `/create/clip`); kaç adım geri
    // gidileceği bilinemez, ama MEVCUT giriş temizlenince yenilemede composer
    // geri gelmez.
    if (olusturModaliMi(modalContent)) olusturUrlTemizle();
    setModalContent(null);
    setModalData(null);
    pushedByAppRef.current = false;
  };

  const handleCreateSelect = (creationType: string) => {
    if (creationType === "mylive") {
      closeModal();
      setActivePage("mylive");
      return;
    }
    // ── P0-4 · HİKAYE: ÖLÜ DÜĞMENİN GERÇEK HEDEFİ ─────────────────────────
    // `<input id="hikaye-upload">` yalnız `Hikayeler` şeridinde var ve o şerit
    // yalnız `activePage === "home"` iken render ediliyor (`App.tsx:1118`).
    // Bu yüzden önce ana sayfaya geçilir, sonra giriş YOKLANARAK açılır (şerit
    // aynı commit'te DOM'da olmayabilir). Bulunamazsa SESSİZ KALINMAZ —
    // sessizlik zaten düzeltilen kusurun kendisiydi.
    if (creationType === "hikaye") {
      closeModal();          // URL: `/create` → `/` (olusturUrlTemizle)
      setActivePage("home"); // hikaye şeridinin yaşadığı tek sekme
      hikayeYuklemeyiAc().then((sonuc) => {
        if (sonuc === "bulunamadi") {
          showToast("Hikaye yükleme açılamadı. Ana sayfadaki hikayendeki + düğmesini dene.");
        }
      });
      return;
    }
    // "+" menüsünden seçim = menünün ÜSTÜNE yeni katman → push. Geri tuşu
    // composer'ı kapatıp menüye döndürür (IG deseni). Bilinmeyen bir tür
    // gelirse URL YAZILMAZ (olusturModaliMi false) — sessizce eski davranış.
    if (olusturModaliMi(creationType)) {
      olusturUrlYaz({ modal: creationType as OlusturModali });
      // Bayat `{kamera:true}` (cold-load `/create/camera`) menüden açılan
      // composer'a sızmasın.
      setModalData(null);
    }
    setModalContent(creationType);
  };

  // EMİR #94.4 — YOK / ULASILAMADI durumu. URL'e DOKUNULMAZ: kullanıcı
  // yenileyip tekrar deneyebilsin (eski replaceState("/") bunu da yok ediyordu).
  // 🔴 EKRAN DIŞINA DÜŞÜYORDU — GÖZLE ÖLÇÜLDÜ (2026-08-24).
  // Var olmayan bir gönderi linki (`/p/yokboyle`) açıldığında kullanıcı
  // DÜPEDÜZ ANA AKIŞ görüyordu. Kart DOM'da vardı ve `data-vw-derin="yok"`
  // kancasını doğru taşıyordu — ama geometrisi mobilde `y=880`, viewport
  // `390×844`, `scrollY=0`. Yani katlamanın **880 px altında**, otomatik
  // kaydırma da yok.
  //
  // Sebep: bu dal ERKEN `return` ediyor ve aşağıdaki (satır ~516)
  // `MODAL_STYLE` sarmalayıcısına (position:fixed; inset:0; z-index:2000)
  // HİÇ girmiyordu. `DerinBaglantiDurumu`nun kendi `min-height:100dvh`i
  // var ama `position`ı yok → normal akışta içeriğin ALTINA yığılıyordu.
  //
  // 🧭 Bu, DOM ölçen bir kapının YEŞİL, gözün KIRMIZI gördüğü tam bir vaka:
  // varlık, görünürlük değildir. Sarmalayıcı eklendi.
  //
  // Arka plan tıklaması BİLEREK kapatmıyor: kartın kendi kapat/tekrar-dene
  // düğmeleri var ve yanlışlıkla kapanması kullanıcıyı yine akışta bırakır.
  if (modalContent === "derinBaglantiDurumu")
    return (
      <div style={MODAL_STYLE} data-vw-derin-kap="1">
      <DerinBaglantiDurumu
        durum={modalData?.derinDurum === "ulasilamadi" ? "ulasilamadi" : "yok"}
        // Ölü rota kapısı (`appRouting.oluRotaMi`) `derinTur: "rota"` yazar →
        // kart "Sayfa bulunamadı" der. Yazılmadığında (tüm mevcut çağrı
        // yerleri) varsayılan "icerik" → metin BİREBİR eskisi gibi kalır.
        tur={modalData?.derinTur === "rota" ? "rota" : "icerik"}
        // #888 — TAM YENİLEME ARTIK SON ÇARE. Önce aynı yol SPA içinde
        // yeniden çözülür (uygulama durumu, feed konumu, oynatma korunur);
        // yalnız çözücü yolu hiç tanımıyorsa `location.reload()`a düşülür.
        onRetry={() => {
          derinBaglantiyiYenidenDene({
            setActivePage,
            setModalContent,
            setModalData,
            setProfileRouteUsername,
            pushedByAppRef,
          });
        }}
        onClose={() => {
          closeModal();
          try { window.history.replaceState({}, "", "/"); } catch { /* no-op */ }
        }}
      />
      </div>
    );

  if (modalContent === "createMenu")
    return <CreateMenu onClose={closeModal} onSelect={handleCreateSelect} />;

  if (modalContent === "newclip") {
    const handleComplete = (_clipId?: unknown) => {
      closeModal();
      // V3 feed'e yönlendir (clips page V3Gate'i içeriyor)
      setActivePage("clips");
    };
    return (
      <React.Suspense fallback={null}>
        <UploadComposerV3
          onClose={closeModal}
          onPublished={handleComplete}
          // `/create/camera` cold-load'u → composer kamerayla açılır. Diğer tüm
          // yollarda (menü, `/create/clip`) `modalData` null → false.
          initialCameraOpen={
            !!(modalData && typeof modalData === "object" && (modalData as { kamera?: boolean }).kamera)
          }
        />
      </React.Suspense>
    );
  }

  if (modalContent === "notifications")
    return <Bildirimler aktifKullaniciId={user.uid} onClose={closeModal} />;

  if (modalContent === "checkin")
    return (
      <CheckInModal
        onClose={closeModal}
        currentUser={user}
        onPlaceSelect={handlePlaceSelectForCheckIn}
      />
    );

  if (modalContent === "newCheckInDetail")
    return (
      <NewCheckInDetail
        selectedPlace={modalData}
        currentUser={user}
        onClose={closeModal}
      />
    );

  if (modalContent === "placeDetail") {
    // Mobil/masaüstü bileşen seçimi — hikâye modalıyla AYNI desen (bkz. bu
    // dosyada `isMobile ? StoryModalMobile : StoryModalDesktop`). Hikâyede
    // iki bileşen aynı props nesnesini alıyor; MEKÂNDA props API'leri FARKLI
    // (`placeData` vs `placeId/placeName/address/coords`), bu yüzden burada
    // küçük bir uyarlama var — desen aynı, sözleşme farklı.
    //
    // ⚠️ Bu devir, mobil modal `resolvePlaceDisplayName`i alana KADAR
    // BİLEREK yapılmamıştı (#782): cold-load'da payload yalnız `placeId`
    // taşır, mobil başlık BOŞ kalırdı. Şart karşılandı ve
    // `mobilMekanAdi.test.ts` ile kanıtlandı; devir ancak ondan sonra.
    //
    // `address`/`coords` global yolda YOK — bunlar yalnız `MapMobile`ın yerel
    // Google Places verisinde vardı ve `/place/{id}` sözleşmesinde yer almıyor.
    // Mobil modal ikisini de `&&` ile süzüyor (satır 340/345/360), yani
    // yokluklarında adres satırı ve yol tarifi bağlantısı GÖSTERİLMEZ.
    // Kayba giren tek şey bu; başlık/check-in/rozet/sekmeler etkilenmiyor.
    if (isMobile)
      return (
        <PlaceDetailModalMobile
          placeId={modalData?.placeId}
          placeName={modalData?.placeName || modalData?.name}
          address={modalData?.address || ""}
          coords={modalData?.coords || null}
          onClose={closeModal}
        />
      );
    return (
      <PlaceDetailModal
        placeData={modalData}
        onClose={closeModal}
        onUserClick={handleViewUserFromPlace}
      />
    );
  }

  if (modalContent === "discovery") {
    // Modal ilk açılınca görseli seç ve cache'le — re-render'larda aynı görsel kalsın
    if (!_lastDiscoveryOpen) {
      _cachedMertImg = MERT_IMGS[Math.floor(Math.random() * MERT_IMGS.length)];
      _lastDiscoveryOpen = true;
      const preloadImg = new window.Image();
      preloadImg.src = _cachedMertImg;
    }
    const mertImg = _cachedMertImg;
    return (
      <div style={{ '--mert-img': `url(${mertImg})`, display: 'contents' } as React.CSSProperties}>
        <DiscoveryMobile
          onClose={closeModal}
          onStartMessage={handleStartMessage}
          initialMatchId={modalData?.matchId || null}
        />
      </div>
    );
  }
  // Discovery kapandıysa cache'i sıfırla
  if (_lastDiscoveryOpen) {
    _lastDiscoveryOpen = false;
    _cachedMertImg = null;
  }

  if (modalContent === "mapSettings")
    return (
      <MapSettingsModal
        onClose={closeModal}
        onOpenFriendPicker={handleOpenFriendPicker}
      />
    );

  if (modalContent === "friendPicker")
    return (
      <FriendPickerModal
        currentUser={currentUserProfile}
        onSave={handleSaveFriendSelection}
        onClose={() => setModalContent("mapSettings")}
      />
    );

  if (modalContent === "viewingProfile") {
    return (
      <KullaniciProfili
        userId={modalData}
        aktifKullaniciId={user.uid}
        onClose={closeModal}
        onUserClick={handleViewProfile}
        onSendMessage={handleStartMessage}
        // #832 — check-in mekân butonu bu yüzeyde de ölüydü; üretici bağlandı.
        // Profil modalı → mekân modalı geçişi `handleViewPlaceDetail`in
        // `modalReturnRef` desenini kullanır: GERİ/X profile geri döner.
        onPlaceClick={handleViewPlaceDetail}
      />
    );
  }

  if (modalContent === "viewingStory") {
    // DENETİM FIX — guard'sız .map tüm App'i düşürebiliyordu
    // ⚠️ Aşağıdaki dört `as` SALT TİP. Gerekçe: `Array.isArray(modalData?.stories)`
    // guard'ı TS 4.9'da TABAN değişkeni (`modalData`) non-null'a daraltmıyor,
    // yalnız zinciri daraltıyor. `?.` EKLEMEK yasak (davranış); guard'ı
    // değiştirmek de yasak. Bu dalın kaynak metni HİÇBİR sözleşme testince
    // aranmıyor (aranan yalnız `placeId={modalData?.placeId}` ve
    // `initialMatchId={modalData?.matchId || null}` — ikisi de dokunulmadı).
    // Derlenen JS orijinaliyle BİREBİR aynı.
    if (!Array.isArray(modalData?.stories) ||
        (modalData as { stories: unknown[] }).stories.length === 0) return null;
    const storiesWithAuthorInfo = (modalData as {
      stories: Array<Record<string, unknown>>;
    }).stories.map((story) => ({
      ...story,
      authorUsername: (modalData as ModalNesnesi).kullaniciAdi,
      authorProfilePic: (modalData as ModalNesnesi).profilFoto,
    }));
    // Cold-load / V3-avatar yolu tek-yazar → çoklu-kullanıcı navigasyonu yok
    // (onNextUser/onPrevUser no-op, hasNext/Prev false).
    const storyModalProps: any = {
      stories: storiesWithAuthorInfo,
      onClose: closeModal,
      initialIndex: modalData?.initialIndex || 0,
      onNextUser: () => {},
      onPrevUser: () => {},
      hasNextUser: false,
      hasPrevUser: false,
    };
    return isMobile ? (
      <StoryModalMobile {...storyModalProps} />
    ) : (
      <StoryModalDesktop {...storyModalProps} />
    );
  }

  const isCommentModal = modalContent === "viewingComments";
  const currentStyle =
    isCommentModal && isMobile ? COMMENTS_MODAL_STYLE : MODAL_STYLE;

  // GÖNDERI DENETIMI P1 — yükleme sürerken backdrop tıklaması composer'ı
  // kapatamaz: upload/addDoc arka planda devam edip "hayalet post"
  // üretiyordu (kullanıcı iptal sandığı gönderi sessizce yayınlanıyordu).
  const handleBackdropClose = () => {
    if (modalContent === "newpost" && isPostUploading()) return;
    closeModal();
  };

  return (
    <div style={currentStyle} onMouseDown={handleBackdropClose}>
      <div onMouseDown={(e) => e.stopPropagation()}>
        {modalContent === "newpost" && <NewPost onClose={closeModal} />}
        {modalContent === "messages" && (
          <DmInbox
            initialUserId={typeof modalData === "string" ? modalData : null}
            initialConversationId={
              modalData && typeof modalData === "object" ? modalData.conversationId : null
            }
            onClose={closeModal}
          />
        )}
        {modalContent === "viewingComments" && (() => {
          // Read the nav context set by UserPosts.openPost (desktop path).
          // readPostNavContext() is stable across re-renders of the open modal
          // and returns null for cold deep-link opens (no list → arrows hidden).
          const navCtx = readPostNavContext();
          return (
            <PostDetailModal
              post={modalData}
              onClose={closeModal}
              aktifKullaniciId={user.uid}
              postList={navCtx?.postList}
              startIndex={navCtx?.startIndex}
              onUserClick={(uid?: string) => {
                closeModal();
                handleViewProfile(uid);
              }}
            />
          );
        })()}
        {modalContent === "viewingClip" && (
          <>
            {isMobile ? (
              <ClipDetailModalMobile clip={modalData} onClose={closeModal} />
            ) : (
              <ClipDetailModalDesktop clip={modalData} onClose={closeModal} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
